import { useState, useEffect, useMemo } from "react";
import * as satellite from "satellite.js";
import { SatelliteTleData, OrbitProfile } from "../../SatDataLoader/types";
import { normalizeTleExponents } from "../../utils/normalizeTleExponents";
import { parseTleLines } from "../../utils/parseTleLines";
import { startVisibleInterval } from "../../utils/startVisibleInterval";
import { EARTH_RADIUS_KM, getOrbitPosition } from "../utils/orbitGeometry";

export type CoveragePointData = {
  kind: "satellite";
  lat: number;
  lng: number;
  alt: number;
  radius: number;
  name: string;
  satellite: SatelliteTleData;
};

const COVERAGE_RADIUS_BY_ORBIT: Record<OrbitProfile, number> = {
  standard: 745,
  "block2-520": 1070,
  "block2-690": 1334,
};

export const useSatelliteCoverage = (
  satTleData: SatelliteTleData[],
  simulationTime: number | null = null,
) => {
  const [, refresh] = useState(0);
  const simulating = simulationTime !== null;
  const sources = useMemo(
    () =>
      satTleData.flatMap((sat) => {
        const lines = parseTleLines(sat.tle);
        if (!lines) return [];
        const radiusKm =
          COVERAGE_RADIUS_BY_ORBIT[sat.info.orbitProfile ?? "standard"];
        return [
          {
            satrec: satellite.twoline2satrec(
              normalizeTleExponents(lines[0]),
              lines[1],
            ),
            point: {
              kind: "satellite" as const,
              lat: 0,
              lng: 0,
              alt: 0,
              radius: (radiusKm / EARTH_RADIUS_KM) * (180 / Math.PI),
              name: sat.info.satname,
              satellite: sat,
            },
          },
        ];
      }),
    [satTleData],
  );

  useEffect(() => {
    if (simulating) return;
    return startVisibleInterval(() => refresh((tick) => tick + 1), 250);
  }, [simulating]);

  const time = simulationTime ?? Date.now();
  const coveragePointData = sources.flatMap(({ satrec, point }) => {
    try {
      const position = getOrbitPosition(satrec, time);
      if (!position) return [];
      [point.lat, point.lng, point.alt] = position;
      return [point];
    } catch {
      return [];
    }
  });
  return { coveragePointData };
};
